import { useMemo } from "react"
import { Segmented, Select, Tag } from "antd"
import type { TenantSummary } from "@csbi/contracts"
import { ENV_LABELS, ENV_ORDER } from "../../shared/env-labels.js"

export const ALL = "*"
export const isAllCustomers = (scope: readonly string[]) => scope.includes(ALL)

/*
 * ⚠ This used to be a text box taking comma-separated deployment ids. A mistyped id is accepted
 * by every layer — it is a valid string, and the scope is only ever used to narrow a query — so
 * the person ends up seeing nothing at all, with no error anywhere to explain why. Choosing from
 * the deployments that exist makes that failure unrepresentable.
 */
export type ScopePickerProps = {
  tenants: readonly TenantSummary[]
  value: readonly string[]
  onChange: (next: string[]) => void
  loading?: boolean
  size?: "small" | "middle"
}

export function ScopePicker({ tenants, value, onChange, loading, size = "middle" }: ScopePickerProps) {
  const all = isAllCustomers(value)

  const options = useMemo(() => {
    const live = tenants.filter((t) => !t.churned)
    const churned = tenants.filter((t) => t.churned)
    const label = (t: TenantSummary) => ({ value: t.id, label: t.displayName })

    const groups = ENV_ORDER.filter((env) => live.some((t) => t.environment === env)).map((env) => ({
      label: ENV_LABELS[env] ?? env,
      options: live.filter((t) => t.environment === env).map(label),
    }))
    // Churned customers stay selectable: someone may still need to look at the history of an account that left
    if (churned.length) groups.push({ label: "Churned", options: churned.map(label) })
    return groups
  }, [tenants])

  const selected = all ? [] : value.filter((id) => id !== ALL)
  const missing = selected.filter((id) => !tenants.some((t) => t.id === id))

  return (
    <>
      <Segmented
        size={size}
        value={all ? "all" : "some"}
        onChange={(next) => onChange(next === "all" ? [ALL] : selected)}
        options={[
          { value: "all", label: "All customers" },
          { value: "some", label: "Only the ones I choose" },
        ]}
      />
      {!all && (
        <Select
          mode="multiple"
          size={size}
          loading={loading ?? false}
          value={selected}
          onChange={onChange}
          options={options}
          style={{ width: "100%", marginTop: 8 }}
          placeholder="Choose the deployments this person may see"
          optionFilterProp="label"
          maxTagCount="responsive"
        />
      )}
      {/* An id with no deployment behind it: kept and shown rather than dropped, because silently
          discarding it would change someone's access without telling anyone it happened */}
      {missing.length > 0 && (
        <div style={{ marginTop: 6 }}>
          {missing.map((id) => (
            <Tag key={id} color="warning">
              {id} — no such deployment
            </Tag>
          ))}
        </div>
      )}
    </>
  )
}
